import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CommunityMap from "../components/CommunityMap";
import SlideOver from "../components/SlideOver";
import { getCommunitiesByGuild, getGuildById } from "../data";

const GuildMap = () => {
  const { guildId } = useParams();
  const guild = getGuildById(guildId);
  const communities = useMemo(() => (guild ? getCommunitiesByGuild(guild.id) : []), [guild]);

  const [filters, setFilters] = useState({ platform: "all", region: "all", tag: "all" });
  const [selected, setSelected] = useState(null);

  const filteredCommunities = useMemo(() => {
    return communities.filter((community) => {
      if (filters.platform !== "all" && !community.platforms.some((platform) => platform.type === filters.platform))
        return false;
      if (filters.region !== "all" && community.region !== filters.region) return false;
      if (filters.tag !== "all" && !community.tags.includes(filters.tag)) return false;
      return true;
    });
  }, [communities, filters]);

  const center = useMemo(() => {
    if (!communities.length) return [20, 0];
    const avgLat = communities.reduce((sum, item) => sum + item.lat, 0) / communities.length;
    const avgLng = communities.reduce((sum, item) => sum + item.lng, 0) / communities.length;
    return [avgLat, avgLng];
  }, [communities]);

  if (!guild) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl text-slate-100">Guild not found</h1>
        <Link to="/directory" className="text-brand-primary underline-offset-4 hover:underline">
          Return to directory
        </Link>
      </div>
    );
  }

  const platformOptions = Array.from(
    new Set(communities.flatMap((community) => community.platforms.map((platform) => platform.type)))
  ).sort();
  const regionOptions = Array.from(new Set(communities.map((community) => community.region))).sort();
  const tagOptions = Array.from(new Set(communities.flatMap((community) => community.tags))).sort();

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-8">
      <header className="space-y-4">
        <p className="eyebrow">Guild Mini-Map</p>
        <h1 className="text-4xl text-slate-100">{guild.name}</h1>
        <p className="text-slate-300">Explore every community in this guild on a focused Leaflet map with filters.</p>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link to={`/guilds/${guild.id}`} className="btn-secondary">
            Back to Guild Hub
          </Link>
          <Link to="/map" className="btn-secondary">
            Global Map
          </Link>
        </div>
      </header>

      <section className="panel space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Platform</label>
            <select
              value={filters.platform}
              onChange={(event) => updateFilter("platform", event.target.value)}
              className="mt-2 w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100"
            >
              <option value="all">All platforms</option>
              {platformOptions.map((platform) => (
                <option key={platform} value={platform}>
                  {platform}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Region</label>
            <select
              value={filters.region}
              onChange={(event) => updateFilter("region", event.target.value)}
              className="mt-2 w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100"
            >
              <option value="all">All regions</option>
              {regionOptions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Tags</label>
            <select
              value={filters.tag}
              onChange={(event) => updateFilter("tag", event.target.value)}
              className="mt-2 w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100"
            >
              <option value="all">All tags</option>
              {tagOptions.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-sm text-slate-400">{filteredCommunities.length} communities shown on the map.</p>
      </section>

      <div className="relative min-h-[480px]">
        <CommunityMap communities={filteredCommunities} center={center} zoom={3} onSelect={setSelected} />
        <SlideOver isOpen={Boolean(selected)} community={selected} guild={guild} onClose={() => setSelected(null)} />
      </div>
    </div>
  );
};

export default GuildMap;
